<template>
  <div class="ra-container">
    <div class="header-row">
      <div class="search-box">
        <input
          type="text"
          v-model="searchQuery"
          :placeholder="$t('raIncomingMail.search.placeholder')"
          @input="filterMails"
        />
        <i class="fas fa-search"></i>
      </div>
      <button class="action-btn upload" @click="showUploadModal = true">
        <i class="fas fa-upload"></i>
        <span>{{ $t('raIncomingMail.actions.upload') }}</span>
      </button>
    </div>

    <div v-if="error" class="error-message">
      <i class="fas fa-exclamation-circle"></i>
      {{ error }}
    </div>

    <div v-if="loading" class="loading-state">
      <i class="fas fa-circle-notch fa-spin"></i>
      <p>Loading incoming mail...</p>
    </div>

    <div v-else class="table-container">
      <table class="mail-table">
        <thead>
          <tr>
            <th>{{ $t('raIncomingMail.table.business') }}</th>
            <th>{{ $t('raIncomingMail.table.type') }}</th>
            <th>{{ $t('raIncomingMail.table.priority') }}</th>
            <th>{{ $t('raIncomingMail.table.status') }}</th>
            <th>{{ $t('raIncomingMail.table.received') }}</th>
            <th>{{ $t('raIncomingMail.table.description') }}</th>
            <th>{{ $t('raIncomingMail.table.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="filteredMails.length === 0">
            <td colspan="7" class="no-data">
              {{ searchQuery ? $t('raIncomingMail.noSearchResults') : $t('raIncomingMail.noMail') }}
            </td>
          </tr>
          <tr v-else v-for="mail in filteredMails" :key="mail.id">
            <td>{{ getBusinessName(mail.business) }}</td>
            <td><span :class="['mail-type-badge', mail.mailType]">{{ mail.mailType }}</span></td>
            <td><span :class="['priority-badge', mail.priority]">{{ mail.priority }}</span></td>
            <td><span :class="['status-badge', mail.status]">{{ mail.status }}</span></td>
            <td>{{ formatDate(mail.receivedDate) }}</td>
            <td>{{ mail.description || 'No description' }}</td>
            <td>
              <button class="action-btn view" @click="viewMail(mail)">
                <i class="fas fa-eye"></i>
                <span>{{ $t('raIncomingMail.actions.view') }}</span>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>    <!-- Upload Modal -->
    <RAUploadMail 
      v-if="showUploadModal"
      :currentLanguage="currentLanguage"
      @close="showUploadModal = false"
      @mail-uploaded="onMailUploaded"
    />

    <!-- View Modal -->
    <div v-if="selectedMail" class="modal-overlay" @click="closeViewModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h2>{{ $t('raIncomingMail.viewModal.title') }}</h2>
          <button @click="closeViewModal" class="close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="mail-details">
            <div class="detail-row">
              <label>{{ $t('raIncomingMail.viewModal.business') }}:</label>
              <span>{{ getBusinessName(selectedMail.business) }}</span>
            </div>
            <div class="detail-row">
              <label>{{ $t('raIncomingMail.viewModal.type') }}:</label>
              <span :class="['mail-type-badge', selectedMail.mailType]">{{ selectedMail.mailType }}</span>
            </div>
            <div class="detail-row">
              <label>{{ $t('raIncomingMail.viewModal.priority') }}:</label>
              <span :class="['priority-badge', selectedMail.priority]">{{ selectedMail.priority }}</span>
            </div>
            <div class="detail-row">
              <label>{{ $t('raIncomingMail.viewModal.status') }}:</label>
              <span :class="['status-badge', selectedMail.status]">{{ selectedMail.status }}</span>
            </div>
            <div class="detail-row">
              <label>{{ $t('raIncomingMail.viewModal.received') }}:</label>
              <span>{{ formatDate(selectedMail.receivedDate) }}</span>
            </div>
            <div class="detail-row">
              <label>{{ $t('raIncomingMail.viewModal.description') }}:</label>
              <span>{{ selectedMail.description || 'No description' }}</span>
            </div>
            <div class="attachments-section">
              <h3>{{ $t('raIncomingMail.viewModal.attachments') }}</h3>
              <div class="attachments-list">
                <div v-for="(attachment, index) in selectedMail.attachments" :key="index" class="attachment-item">
                  <i class="fas fa-file-alt"></i>
                  <span class="attachment-name">{{ attachment.originalName }}</span>
                  <button class="download-btn" @click="downloadAttachment(attachment)">
                    <i class="fas fa-download"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import RAUploadMail from './RAUploadMail.vue';
import incomingMailService from '@/services/incomingMailService';

export default {
  name: 'RAIncomingMail',
  components: {
    RAUploadMail
  },
  props: {
    currentLanguage: {
      type: String,
      default: 'English'
    }
  },
  data() {
    return {
      mails: [],
      filteredMails: [],
      searchQuery: '',
      loading: false,
      error: null,
      showUploadModal: false,
      selectedMail: null
    }
  },
  watch: {
    currentLanguage: {
      immediate: true,
      handler(newLanguage) {
        const lang = newLanguage === 'English' ? 'en' : 'es'
        this.$i18n.locale = lang
      }
    }
  },
  methods: {
    async fetchMails() {
      this.loading = true;
      this.error = null;
      try {
        const response = await incomingMailService.getIncomingMail();
        if (response.success) {
          this.mails = Array.isArray(response.data) ? response.data : [];
          this.filterMails();
        } else {
          throw new Error(response.error || 'Failed to fetch incoming mail');
        }
      } catch (error) {
        console.error('Error fetching incoming mail:', error);
        this.error = error.message || 'Failed to load incoming mail. Please try again.';
        this.mails = [];
        this.filteredMails = [];
      } finally {
        this.loading = false;
      }
    },

    filterMails() {
      const query = this.searchQuery.toLowerCase().trim();
      if (!query) {
        this.filteredMails = [...this.mails];
        return;
      }
      
      this.filteredMails = this.mails.filter(mail => {
        return (
          this.getBusinessName(mail.business).toLowerCase().includes(query) ||
          mail.mailType.toLowerCase().includes(query) ||
          mail.priority.toLowerCase().includes(query) ||
          mail.status.toLowerCase().includes(query) ||
          (mail.description || '').toLowerCase().includes(query)
        );
      });
    },

    getBusinessName(business) {
      if (!business) return 'Unknown Business';
      return business.legalBusinessName || business.tradeName || 'Unnamed Business';
    },

    formatDate(date) {
      if (!date) return 'N/A';
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    viewMail(mail) {
      this.selectedMail = mail;
    },

    closeViewModal() {
      this.selectedMail = null;
    },

    async downloadAttachment(attachment) {
      try {
        await incomingMailService.downloadAttachment(attachment.path);
      } catch (error) {
        console.error('Error downloading attachment:', error);
        this.error = 'Failed to download attachment. Please try again.';
      }
    },

    onMailUploaded(mail) {
      this.mails.unshift(mail);
      this.filterMails();
      this.showUploadModal = false;
    }
  },
  mounted() {
    this.fetchMails();
  }
};
</script>

<style scoped>
.ra-container {
  padding: 2rem;
}

.header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.search-box {
  position: relative;
  display: flex;
  align-items: center;
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.03);
  padding: 0.2rem 1rem 0.2rem 0.8rem;
  margin-right: 1rem;
  width: 300px;
}

.search-box input {
  border: none;
  outline: none;
  background: transparent;
  font-size: 1rem;
  flex: 1;
  padding: 0.6rem 0.5rem 0.6rem 0;
}

.search-box i {
  color: #888;
  font-size: 1.1rem;
  margin-left: 0.3rem;
}

.error-message {
  background-color: #fee2e2;
  color: #dc2626;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.loading-state {
  text-align: center;
  padding: 3rem 1rem;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}

.loading-state i {
  font-size: 2rem;
  color: #006400;
  margin-bottom: 1rem;
}

.loading-state p {
  color: #374151;
  margin: 0.5rem 0;
}

.table-container {
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  overflow: hidden;
}

.mail-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background: #fff;
}

.mail-table th, 
.mail-table td {
  padding: 1rem 1.2rem;
  font-size: 1rem;
  text-align: left;
  vertical-align: top;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 250px; /* Maximum width for any cell */
}

.mail-table th {
  background: #f5f5f5;
  font-weight: 700;
  color: #1a1a1a;
  border-bottom: 1.5px solid #e5e7eb;
}

.mail-table td {
  border-bottom: 1px solid #e5e7eb;
}

.mail-table tr:last-child td {
  border-bottom: none;
}

.mail-type-badge,
.status-badge {
  display: inline-block;
  padding: 0.3em 1em;
  border-radius: 1em;
  font-size: 0.95em;
}

.priority-badge {
  display: inline-block;
  padding: 0.25em 0.75em;
  border-radius: 9999px;
  font-size: 0.85em;
  font-weight: 500;
  margin-left: 0.5rem;
}

.mail-type-badge.legal {
  background: #fee2e2;
  color: #dc2626;
}

.mail-type-badge.tax {
  background: #fef3c7;
  color: #d97706;
}

.mail-type-badge.general {
  background: #e0f2fe;
  color: #0369a1;
}

.priority-badge.low {
  background: #f3f4f6;
  color: #6b7280;
}

.priority-badge.medium {
  background: #e0f2fe;
  color: #0284c7;
}

.priority-badge.high {
  background: #fef3c7;
  color: #d97706;
}

.priority-badge.urgent {
  background: #fee2e2;
  color: #dc2626;
}

.status-badge.received {
  background: #e0f2fe;
  color: #0369a1;
}

.status-badge.processed {
  background: #dcfce7;
  color: #16a34a;
}

.status-badge.forwarded {
  background: #fef3c7;
  color: #d97706;
}

.status-badge.shredded {
  background: #f3f4f6;
  color: #6b7280;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, transform 0.15s;
}

.action-btn.upload {
  background: #006400;
  color: #fff;
}

.action-btn.upload:hover {
  background: #005000;
}

.action-btn.view {
  background: #006400;
  color: #fff;
}

.action-btn.view:hover {
  background: #005000;
}

.no-data {
  text-align: center;
  color: #888;
  font-size: 1rem;
  padding: 2rem 0;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 90%;
  padding: 0;
  max-width: 600px;
  max-height: 90vh;
  overflow: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
}

.modal-header {
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h2 {
  font-size: 1.5rem;
  color: #1f2937;
  margin: 0;
}

.modal-header .close-btn {
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  font-size: 1.5rem;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: all 0.2s;
}

.modal-header .close-btn:hover {
  background: #f3f4f6;
  color: #1f2937;
}

.modal-body {
  padding: 1.5rem;
}

.mail-details {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.detail-row {
  display: flex;
  gap: 1rem;
}

.detail-row label {
  flex: 0 0 120px;
  font-weight: 600;
  color: #374151;
}

.attachments-section {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e5e7eb;
}

.attachments-section h3 {
  font-size: 1.1rem;
  color: #1f2937;
  margin-bottom: 1rem;
}

.attachments-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.attachment-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.attachment-item i {
  color: #6b7280;
}

.attachment-name {
  flex: 1;
  color: #1f2937;
}

.download-btn {
  background: none;
  border: none;
  color: #006400;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  transition: all 0.2s;
}

.download-btn:hover {
  background: #e8f5e9;
}

@media (max-width: 768px) {
  .ra-container {
    padding: 1rem;
  }

  .header-row {
    flex-direction: column;
    gap: 1rem;
  }

  .search-box {
    width: 100%;
    margin-right: 0;
  }

  .action-btn.upload {
    width: 100%;
    justify-content: center;
  }

  .mail-table {
    display: block;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .modal-content {
    width: 95%;
    margin: 1rem;
  }
}
</style>